import { Button, Input, Typography, Spin, Steps, Tooltip, message } from "antd";
import React from "react";
import { useOrg } from "src/hooks/useOrg";
import {
  createSource,
  getSource,
  updateSource,
} from "pages/api-clients/integrations";
import {
  Source,
  SourceType,
} from "pages/api/[orgId]/integrations-client/definitions";

const { Title, Text } = Typography;

enum IntegrationState {
  fetching,
  none,
  creating,
  active,
  savingSlack,
}

export function SlackIntegrationStatus() {
  const { id: orgId } = useOrg();
  const [slackSource, setSlackSource] = React.useState<Source | null>(null);
  const [state, setState] = React.useState<IntegrationState>(
    IntegrationState.fetching
  );
  const [authToken, setAuthToken] = React.useState("");
  const [host, setHost] = React.useState("");
  const fetchedOnce = React.useRef(false);
  const listItems = [
    {
      description: (
        <p>
          Login to &nbsp;
          <a
            href="https://api.slack.com/apps/"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://api.slack.com/apps/
          </a>
        </p>
      ),
    },
    { description: "Click 'Create New App -> From Scratch'" },
    { description: "Add a name for the application" },
    { description: "Use your organization as the workspace" },
    { description: "Select Event Subscription and Enable Events" },
    {
      description: (
        <p>
          For <strong>Request Url</strong> use:
          <Text code copyable>
            {`https://int.vzbl.dev/api/v1/slack/webhook/${slackSource?.uuid}`}
          </Text>
        </p>
      ),
    },
    {
      description: (
        <>
          Add the following events in the bot event subscription section:
          <ol>
            <li>dnd_updated_user</li>
            <li>channel_created</li>
            <li>message.groups</li>
            <li>message.channels</li>
            <li>message.mpim</li>
            <li>user_huddle_changed</li>
            <li>user_status_changed</li>
            <li>user_change</li>
            <li>member_joined_channel</li>
          </ol>
        </>
      ),
    },
    {
      description: (
        <>
          In &quot;Oauth & Permissions&quot; add the following scopes:
          <ol>
            <li>channels:history</li>
            <li>channels:join</li>
            <li>channels:read</li>
            <li>dnd:read</li>
            <li>groups:history</li>
            <li>groups:read</li>
            <li>mpim:history</li>
            <li>mpim:read</li>
            <li>team:read</li>
            <li>users:read</li>
          </ol>
        </>
      ),
    },
    { description: "Install your new Slack bot to your workspace" },
    {
      description: (
        <>
          Go back to &quot; Oauth & Permissions&quot; and copy the &apos;Bot
          User Auth Token&apos; and paste it below
        </>
      ),
    },
  ];

  React.useEffect(() => {
    if (!fetchedOnce.current) {
      return;
    }

    if (slackSource == null) {
      setState(IntegrationState.none);
    } else {
      setState(IntegrationState.active);
    }
  }, [slackSource]);

  const transitionRef = React.useRef({ slackSource, orgId });
  transitionRef.current = { slackSource, orgId };
  React.useEffect(() => {
    const { slackSource, orgId } = transitionRef.current;
    if (!orgId) return;
    switch (state) {
      case IntegrationState.fetching: {
        getSource(orgId, SourceType.Slack).then((payload) => {
          const source = payload.data?.[0];
          fetchedOnce.current = true;
          setSlackSource(source);
          setAuthToken(source?.authToken || "");
          setHost(source?.host || "");
        });
        break;
      }
      case IntegrationState.creating: {
        createSource(orgId, SourceType.Slack).then((payload) => {
          setSlackSource(payload.data);
        });
        break;
      }
      case IntegrationState.savingSlack: {
        updateSource(orgId, SourceType.Slack, {
          id: slackSource!.id,
          authToken,
          host,
        }).then((payload) => {
          const source = payload.data;
          setSlackSource(source);
          setState(IntegrationState.fetching);
          message.success("Successfully updated slack integration");
        });
        break;
      }
    }
  }, [state, authToken, host]);

  return [
    IntegrationState.fetching,
    IntegrationState.creating,
    IntegrationState.savingSlack,
  ].includes(state) ? (
    <Spin />
  ) : state == IntegrationState.none ? (
    <Button
      onClick={() => {
        setState(IntegrationState.creating);
      }}
    >
      Create Slack Integration
    </Button>
  ) : (
    <div style={{ padding: 16 }}>
      <Steps
        direction="vertical"
        progressDot
        items={listItems}
        current={listItems.length - 1}
      />
      <Tooltip placement="topLeft" title="Add the slack host">
        <Title level={5}>Host</Title>
        <Input
          allowClear
          defaultValue={host}
          style={{ width: "80%" }}
          placeholder="https://yourorg.slack.com"
          onChange={(event) => setHost(event.target.value)}
        />
      </Tooltip>
      <Tooltip placement="topLeft" title="Add the bot token">
        <Title level={5}>Configure your bot auth token</Title>
        <Input.Password
          allowClear
          visibilityToggle={authToken != "placeholder"}
          defaultValue={authToken}
          style={{ width: "80%" }}
          onChange={(event) => setAuthToken(event.target.value)}
        />
        <Button
          type="primary"
          style={{ marginLeft: "10px" }}
          onClick={() => {
            setState(IntegrationState.savingSlack);
          }}
          disabled={!authToken && !host}
        >
          Save
        </Button>
      </Tooltip>
    </div>
  );
}
